import { API_BASE_URL } from "src/config/apiBase";
import React, { useState } from "react";
import "../../CSS/Global.css";
import { useSidebar } from "../../SidebarContext";
import Header from "../Header";
import Sidebar from "../SideBar";
import { readFirstSheetAsJson } from "../../utils/excelSheet";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";

const ExpenseImport = () => {

    const [currentStep, setCurrentStep] = useState(1);
    const [previewData, setPreviewData] = useState([]);
    const [excelData, setExcelData] = useState([]);
    const [fileName, setFileName] = useState("");
    const [skippedRecords, setSkippedRecords] = useState([]);
    const [validInvoices, setValidInvoices] = useState([]);
    const [failedInvoices, setFailedInvoices] = useState([]);
    const [unmappedFields, setUnmappedFields] = useState([]);
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [fieldMappings, setFieldMappings] = useState({});
    const [excelHeaders, setExcelHeaders] = useState([]);
    const [rawExcelData, setRawExcelData] = useState([]);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const navigate = useNavigate();
    const url = API_BASE_URL
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const token = decryptToken("accessToken") || ""

    const [importStats, setImportStats] = useState({
        count: 0,
        skipped: 0,
        unmapped: 0,
    });

    // const handleNext = () => {
    //     if (currentStep === 2) checkUnmappedFields(); // Check before going to step 3
    //     if (currentStep < 3) setCurrentStep(currentStep + 1);
    // };
    const handleNext = () => {
        if (currentStep === 2) {
            checkUnmappedFields(); // Set importStats.unmapped

            const requiredFields = ["customerName", "phone1"];
            const mappedData = rawExcelData.map((row) => {
                const obj = {};
                fields.forEach((fieldKey) => {
                    const mappedHeader = fieldMappings[fieldKey];
                    if (!mappedHeader || mappedHeader === "none") return;

                    const value = row[mappedHeader] || "";
                    if (fieldKey.includes(".")) {
                        const [parent, child] = fieldKey.split(".");
                        if (!obj[parent]) obj[parent] = {};
                        obj[parent][child] = value;
                    } else {
                        obj[fieldKey] = value;
                    }
                });
                obj.user_id = isLoggedIn?.main_id || "";
                return obj;
            });

            // ✅ Validate mapped fields
            const valid = mappedData.filter((cust) =>
                requiredFields.every((f) => {
                    if (f.includes(".")) {
                        const [p, c] = f.split(".");
                        return cust[p] && cust[p][c];
                    }
                    return cust[f];
                })
            );

            setValidInvoices(valid);

            setImportStats(prev => ({
                ...prev,
                count: valid.length,
                skipped: mappedData.length - valid.length
            }));
        }

        if (currentStep < 3) setCurrentStep(currentStep + 1);
    };



    const handleBack = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    const convertExcelDate = (serial) => {
        const baseDate = new Date(1899, 11, 30);
        const corrected = new Date(baseDate.getTime() + (serial + 1) * 86400000);
        return corrected.toISOString().split("T")[0];
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setUploadSuccess(false);
            return;
        }

        const allowedExtensions = ["csv", "xlsx", "xls"];
        const fileExtension = file.name.split(".").pop().toLowerCase();

        if (!allowedExtensions.includes(fileExtension)) {
            toast.error("Invalid file format. Please upload a CSV, XLSX, or XLS file.", {
                position: "top-center",
                autoClose: 3000
            });
            e.target.value = "";
            return;
        }

        const reader = new FileReader();
        reader.onload = async (evt) => {
            let data;
            try {
                data = await readFirstSheetAsJson(evt.target.result);
            } catch (err) {
                console.error(err);
                toast.error("Could not read spreadsheet file.", { position: "top-center", autoClose: 3000 });
                return;
            }
            const headers = Object.keys(data[0] || {});
            setExcelHeaders(headers);
            setRawExcelData(data); // new state

            // Auto-map headers to field mappings
            const autoMapped = {};
            fields.forEach((field) => {
                const label = fieldLabels[field];
                const match = headers.find(h => h.trim().toLowerCase() === label.trim().toLowerCase());
                if (match) {
                    autoMapped[field] = match;
                }
            });
            setFieldMappings(autoMapped);


            const parsed = [];
            const reasonSet = new Set();

            const requiredFields = ["Date", "Amount"];

            data.forEach((row, index) => {
                let hasMissing = false;

                requiredFields.forEach((field) => {
                    if (!(field in row) || row[field] === "") {
                        reasonSet.add(`Row ${index + 2}: Missing or empty "${field}"`);
                        hasMissing = true;
                    }
                });

                parsed.push({
                    date: row["Date"] || "",
                    expenseType: row["Type"] || "",
                    category: row["Category"] || "",
                    receipt: row["Receipt"] || "",
                    amount: row["Amount"] || "",
                    invoiceNumber: row["Invoice Number"] || "",
                    notes: row["Notes"] || "",
                    customer: row["Customer"] || "",
                });
            });

            setPreviewData(parsed);
            setExcelData(parsed); // ✅ Save parsed data to send to backend

            const valid = parsed.filter((_, i) => {
                const original = data[i];
                return requiredFields.every(f => f in original && original[f]);
            });
            setImportStats(prev => ({
                ...prev,
                count: valid.length
            }));

            setValidInvoices(valid);
            setSkippedRecords([...reasonSet]);

            const unmappedOnly = Array.from(reasonSet).filter(reason =>
                reason.includes("is not a number")
            );
            setUnmappedFields(unmappedOnly);

            const skippedOnly = Array.from(reasonSet).filter(reason =>
                !reason.includes("is not a number")
            );
            setSkippedRecords(skippedOnly);
        };
        setUploadSuccess(true);
        reader.readAsBinaryString(file);
    };


    const handleImport = async () => {
        try {
            const remappedData = rawExcelData.map((row) => {
                const mappedRow = {};

                fields.forEach((fieldKey) => {
                    const mappedHeader = fieldMappings[fieldKey]; // e.g. Display Name
                    if (!mappedHeader || mappedHeader === "none") return;

                    if (fieldKey.includes(".")) {
                        const [parent, child] = fieldKey.split(".");
                        if (!mappedRow[parent]) mappedRow[parent] = {};
                        mappedRow[parent][child] = row[mappedHeader] || "";
                    } else {
                        mappedRow[fieldKey] = row[mappedHeader] || "";
                    }
                });

                mappedRow.user_id = isLoggedIn?.main_id || "";

                return mappedRow;
            });

            const res = await fetch(`${url}/api/expenses/import-bulk`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ customers: remappedData })
            });

            const data = await res.json();
            if (res.ok) {
                setImportStats({
                    count: data.count || 0,
                    skipped: data.skipped || 0,
                    unmapped: data.unmapped || 0,
                });
                toast.success(data.message + " Total: " + data.count, {
                    position: "top-center",
                    autoClose: 2000
                });
                navigate("/expense")

            }
            // alert(data.message + " Total: " + data.count);
        } catch (err) {
            console.error(err);
            alert("Import failed");
        }
    };





    const checkUnmappedFields = () => {
        const total = fields.length;
        const mappedCount = Object.values(fieldMappings).filter(val => val && val !== "none").length;
        const unmapped = total - mappedCount;

        setImportStats(prev => ({ ...prev, unmapped }));
    };


    const options = [
        { value: "Date", label: "Date" },
        { value: "Category", label: "Category" },
        { value: "Receipt", label: "Receipt" },
        { value: "Amount", label: "Amount" },
        { value: "Invoice Number", label: "Invoice Number" },
        { value: "Notes", label: "Notes" },
        { value: "Customer", label: "Customer" },

    ];


    const fields = [
        "date",
        "expenseType",
        "category",
        "receipt",
        "amount",
        "invoiceNumber",
        "notes",
        "customer",
    ];


    const fieldLabels = {
        date: "Date",
        expenseType: "Type",
        category: "Category",
        receipt: "Receipt",
        amount: "Amount",
        invoiceNumber: "Invoice Number",
        notes: "Notes",
        customer: "Customer",
    };




    return (
        <>
            <h2 className="customer-import">Expense - select file</h2>

            {/* Step Indicators */}
            <div className="import-step">
                {[1, 2, 3].map(step => (
                    <div
                        key={step}
                        className={`import-step__item ${currentStep === step ? "active" : currentStep > step ? "past" : "upcoming"}`}
                    >
                        {step}
                    </div>
                ))}
            </div>

            {/* Step 1: Upload */}
            {currentStep === 1 && (
                <div className="customer-import-card">
                    <div className="import-icon">
                        <i className="fa-solid fa-file-arrow-down"></i>
                    </div>
                    <p className="scan-drop">Drag and drop file to import</p>
                    <div className="upload-file">
                        <label htmlFor="file" className="upload-border">
                            <i className="fa-solid fa-file-arrow-down"></i> Import file
                            <input
                                type="file"
                                id="file"
                                name="file"
                                className="file-input"
                                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                onChange={handleFileUpload}
                            />
                        </label>
                    </div>
                    {uploadSuccess && (
                        <p className="upload-success-message">
                            ✅ File <strong>{fileName}</strong> uploaded successfully!
                        </p>
                    )}

                    <p className="file-type">
                        Maximum File Size: 25 MB • File Format: CSV, TSV, or XLS
                    </p>
                    <p className="example-file">
                        Download a sample csv or xls file and compare it to your import file to ensure compatibility.
                    </p>
                </div>
            )}

            {/* Step 2: Mapping */}
            {currentStep === 2 && (
                <div className="customer-import-card">
                    <p className="selected-file">
                        Your Selected File: {fileName}
                    </p>
                    <div className="import-mapping">
                        <div className="import-mapping-label mapping-label-head">
                            Beentara field
                        </div>
                        <div className="import-mapping-select mapping-selecy-head">
                            Import file field
                        </div>
                    </div>
                    {fields.map((field, index) => (
                        <div className="import-mapping" key={index}>
                            <div className="import-mapping-label">{fieldLabels[field]}:</div>

                            <div className="import-mapping-select">
                                <select
                                    value={fieldMappings[field] || "none"}
                                    onChange={(e) => {
                                        const newMappings = { ...fieldMappings, [field]: e.target.value };
                                        setFieldMappings(newMappings);
                                    }}
                                >
                                    <option value="none">-- Select Field --</option>
                                    {excelHeaders.map((header, idx) => (
                                        <option key={idx} value={header}>
                                            {header}
                                        </option>
                                    ))}
                                </select>


                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Step 3: Summary */}
            {currentStep === 3 && (
                <div className="customer-import-card">
                    <p className="import-success">
                        Customers ready to import: {importStats.count}
                    </p>
                    <p className="import-error">
                        Skipped Records: {importStats.skipped}
                    </p>
                    <p className="import-error">
                        Unmapped Fields: {importStats.unmapped}
                    </p>
                </div>
            )}

            {/* Buttons */}
            <div className="import-buttons">
                {currentStep > 1 && (
                    <button className="import-back" onClick={handleBack}>
                        Back
                    </button>
                )}
                {currentStep === 3 ? (
                    <button className="import-button" onClick={handleImport}>
                        Import
                    </button>
                ) : (
                    <button className="import-next" onClick={handleNext}>
                        Next
                    </button>
                )}
            </div>
        </>
    );
};

export default ExpenseImport;
